import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { CarouselProvider, Slider, Slide, Dot } from 'pure-react-carousel';

import Row from '~/layout/Row';
import Column from '~/layout/Column';

import OffScreenScroller from '~/components/OffScreenScroller';

import useWindowSize from '~/hooks/useWindowSize';

import { h3, p } from '~/styles/typography';
import { transition } from '~/styles/transition';
import { calcVw } from '~/styles/helpers';

import { stripTags, dynamicFontSize, formatText } from '~/utilities/helpers';

const Section = styled.section`
    padding: 50px 0;
    position: relative;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 50%;
        right: 50%;
        width: ${calcVw(100)};
        margin: 0 ${calcVw(-50)};
        bottom: 0;
        background-color: ${({ theme }) => theme.colors.accent.blue};
        z-index: ${({ theme }) => theme.zIndex.backgroundGraphics};
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        padding: 75px 0;
    }
`;

const StyledRow = styled(Row)`
    display: none;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        display: grid;
    }
`;

const H3 = styled.h3`
    ${h3};
    color: ${({ theme }) => theme.colors.primary.white};
    text-transform: uppercase;
    text-align: center;
`;

const Description = styled.p`
    ${p};
    color: ${({ theme }) => theme.colors.primary.white};
    text-align: center;
    margin-top: 25px;
    margin-bottom: 60px;
`;

const Card = styled.div`
    background-color: ${({ theme }) => theme.colors.primary.white};
    padding: 20px;
    height: 100%;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        margin-right: 10px;
    }
`;

const AddOnTitle = styled.h4`
    font-family: ${({ theme }) => theme.fonts.heading};
    font-weight: ${({ theme }) => theme.fontWeights.black};
    font-size: ${dynamicFontSize(2.4, 2.8, 3.2)};
    margin-bottom: 15px;
`;

const AddOnDescription = styled.p`
    font-size: ${dynamicFontSize(1.6, 1.7, 2)};
    font-family: ${({ theme }) => theme.fonts.heading};
    font-weight: ${({ theme }) => theme.fontWeights.black};
`;

const DotsWrapper = styled.div`
    display: flex;
    justify-content: center;
    gap: 35px;
    margin-top: 45px;
`;

const StyledDot = styled(Dot)`
    background-color: ${({ theme }) => theme.colors.primary.yellow};
    height: 18px;
    width: 18px;
    border-radius: 50%;
    position: relative;
    ${transition({}, 'background-color')};

    &:hover,
    &:focus {
        background-color: ${({ theme }) => theme.colors.primary.white};
    }

    &.carousel__dot--selected {
        &::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 46px;
            height: 46px;
            border-radius: 50%;
            background-color: ${({ theme }) => theme.colors.primary.red};
            z-index: ${({ theme }) => theme.zIndex.backgroundGraphics};
        }

        &:after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background-color: ${({ theme }) => theme.colors.accent.blue};
            z-index: ${({ theme }) => theme.zIndex.backgroundGraphics};
        }
    }
`;

const ScrollWrapper = styled(OffScreenScroller)`
    display: flex;
    gap: 10px;
    width: ${calcVw(100)};

    ${Card} {
        flex-shrink: 0;
        width: calc(67vw - ${({ theme }) => theme.layout.gutter} * 2);
        height: auto;

        &:first-of-type {
            margin-left: ${({ theme }) => theme.layout.gutter};
        }
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        display: none;
    }
`;

const AddOnCarousel = ({ title, description, addOns }) => {
    const [visibleSlides, setVisibleSlides] = useState(5);
    const scrollableRef = useRef(null);

    const dots = useMemo(() => {
        const dots = [];
        for (let i = 0; i < addOns.length; i = i + visibleSlides) {
            dots.push(<StyledDot span={i} key={addOns[i].id} slide={i} />);
        }
        return dots;
    }, [visibleSlides, addOns]);

    const windowSize = useWindowSize();

    useEffect(() => {
        if (windowSize.includes('largeTablet')) {
            setVisibleSlides(5);
        } else {
            setVisibleSlides(3);
        }
    }, [windowSize]);

    return (
        <Section ref={scrollableRef}>
            <Row>
                <Column span={12} startDesktop={3} spanDesktop={8}>
                    <H3>{title}</H3>
                    {description && <Description>{description}</Description>}
                </Column>
            </Row>
            <StyledRow>
                <Column span={12}>
                    <CarouselProvider
                        naturalSlideWidth={282}
                        naturalSlideHeight={195}
                        totalSlides={addOns.length}
                        visibleSlides={
                            addOns.length > visibleSlides
                                ? visibleSlides
                                : addOns.length
                        }
                        isIntrinsicHeight={true}
                    >
                        <Slider>
                            {addOns.map(
                                (
                                    {
                                        id,
                                        title,
                                        addOnInformation: { shortDescription }
                                    },
                                    index
                                ) => (
                                    <Slide key={id} index={index}>
                                        <Card>
                                            <AddOnTitle>
                                                {formatText(title)}
                                            </AddOnTitle>
                                            <AddOnDescription>
                                                {formatText(
                                                    stripTags(shortDescription)
                                                )}
                                            </AddOnDescription>
                                        </Card>
                                    </Slide>
                                )
                            )}
                        </Slider>
                        {addOns.length > visibleSlides && (
                            <DotsWrapper>{dots}</DotsWrapper>
                        )}
                    </CarouselProvider>
                </Column>
            </StyledRow>
            <ScrollWrapper
                watchElement={scrollableRef}
                rootMargin="-450px 0px -10% 0px"
            >
                {addOns.map(
                    ({ id, title, addOnInformation: { shortDescription } }) => (
                        <Card key={id}>
                            <AddOnTitle>{title}</AddOnTitle>
                            <AddOnDescription>
                                {formatText(stripTags(shortDescription))}
                            </AddOnDescription>
                        </Card>
                    )
                )}
            </ScrollWrapper>
        </Section>
    );
};

AddOnCarousel.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    addOns: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            addOnInformation: PropTypes.shape({
                shortDescription: PropTypes.string.isRequired
            }).isRequired
        })
    ).isRequired
};

export default AddOnCarousel;
